import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {getExtensionSettingsKey} from '@api/model/ui'
import {UIRegistry} from '@api/model/ui-registry'
import {PermissionsChecker} from '@client/permissions-checker'
import {ButtonNew} from '@components/common/base/buttons'
import {Loader} from '@components/common/base/loader'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {useForceUpdate} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {ExtensionStore} from '../../extensions-store'
import {
  handleExtensionDisable,
  handleExtensionEnable
} from '../extension-side-effects'

const TwExtensionActions = tw.div`
  flex
  gap-8
`

type ExtensionActionButtonsProps = {
  extensionStore: ExtensionStore
  onOpenSettings: () => void
  fitWidth?: boolean
  className?: string
}

export const ExtensionActionButtons = observer(
  (props: ExtensionActionButtonsProps) => {
    const {extensionStore, onOpenSettings, className, fitWidth = false} = props

    const forceUpdate = useForceUpdate()
    const getSettingsKey = getExtensionSettingsKey(extensionStore.id)

    const isAdmin = PermissionsChecker.isAdmin()
    const hasSettings =
      isAdmin &&
      extensionStore.enabled &&
      UIRegistry.get(globals.getCompanyId()).get(getSettingsKey)

    useEffect(() => {
      UIRegistry.get(globals.getCompanyId()).addListener(getSettingsKey, () => {
        forceUpdate()
      })

      return () => {
        UIRegistry.get(globals.getCompanyId()).removeListener(getSettingsKey)
      }
    }, [])

    const handleInstallToggle = async () => {
      if (!isAdmin || extensionStore.isInstallInProgress) {
        return
      }

      extensionStore.setInstallProgress(true)

      try {
        if (extensionStore.enabled) {
          await handleExtensionDisable(extensionStore)
        } else {
          await handleExtensionEnable(extensionStore)
        }
      } finally {
        // Timeout is just no enchance UX transition, in case when response
        // was very fast, to avoid flickering loader/button switch
        setTimeout(() => extensionStore.setInstallProgress(false), 300)
      }
    }
    const installButtonLabel = extensionStore.enabled
      ? Lang.msg.extensions.disable
      : Lang.msg.extensions.enable

    if (extensionStore.isInstallInProgress) {
      const loader = <Loader size="small" />

      if (fitWidth) {
        return <div className="flex justify-center">{loader}</div>
      }

      return loader
    }

    let button = (
      <ButtonNew
        disabled={!isAdmin}
        onClick={handleInstallToggle}
        color={extensionStore.enabled ? 'secondary' : 'primary'}
        size="sm"
        className="flex-1"
        fitWidth={fitWidth}
      >
        {installButtonLabel}
      </ButtonNew>
    )

    if (!isAdmin) {
      button = (
        <Tooltip
          content={Lang.msg.extensions.enableOnlyAdmin(extensionStore.enabled)}
          placement="top"
        >
          {button}
        </Tooltip>
      )
    }

    return (
      <TwExtensionActions className={className}>
        {hasSettings && isAdmin && (
          <ButtonNew
            icon="IconSettings"
            disabled={!isAdmin}
            onClick={onOpenSettings}
            color={'secondary'}
            size="sm"
          />
        )}
        {button}
      </TwExtensionActions>
    )
  }
)
